import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './FeedFloToolTip.scss';

/***
 * ? DEPRECATED ?
 * Note: Not recommended to use on _large_ children as % gets thrown off
 */
function FeedFloToolTip({
  children,
  description,
  className = '',
  textClassName = '',
  position = 'top',
  multiline = false,
  darkMode = false,
  ...props
}) {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  // Calculate tooltip position using absolute positioning with container-relative coordinates
  const calculateTooltipPosition = () => {
    if (!tooltipRef.current || !triggerRef.current || !isVisible)
      return { tooltipStyle: { top: -1000, left: -1000 }, actualPosition: 'top' };

    const tooltip = tooltipRef.current;
    const trigger = triggerRef.current;
    const rect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    // Find the tooltip's parent container for relative positioning
    const parentContainer = tooltip.parentElement;
    const parentRect = parentContainer ? parentContainer.getBoundingClientRect() : { top: 0, left: 0 };

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Define position preferences in order of priority
    const positionPreferences = {
      top: ['top', 'bottom', 'left', 'right'],
      bottom: ['bottom', 'top', 'left', 'right'],
      left: ['left', 'right', 'top', 'bottom'],
      right: ['right', 'left', 'top', 'bottom'],
    };

    // Get the preferred position order for the requested position
    const preferences = positionPreferences[position] || [positionPreferences.top];

    let finalPosition = position;
    let viewportTop, viewportLeft;

    // Try each position preference until we find one that fits
    for (const pos of preferences) {
      let fits = true;

      switch (pos) {
        case 'top':
          viewportTop = rect.top - tooltipRect.height - 10;
          viewportLeft = rect.left + rect.width / 2 - tooltipRect.width / 2;

          // Check if this position fits
          if (viewportTop < 10 || viewportLeft < 10 || viewportLeft + tooltipRect.width > viewport.width - 10) {
            fits = false;
          }
          break;

        case 'bottom':
          viewportTop = rect.bottom + 10;
          viewportLeft = rect.left + rect.width / 2 - tooltipRect.width / 2;

          // Check if this position fits
          if (
            viewportTop + tooltipRect.height > viewport.height - 10 ||
            viewportLeft < 10 ||
            viewportLeft + tooltipRect.width > viewport.width - 10
          ) {
            fits = false;
          }
          break;

        case 'left':
          viewportTop = rect.top + rect.height / 2 - tooltipRect.height / 2;
          viewportLeft = rect.left - tooltipRect.width - 10;

          // Check if this position fits
          if (viewportLeft < 10 || viewportTop < 10 || viewportTop + tooltipRect.height > viewport.height - 10) {
            fits = false;
          }
          break;

        case 'right':
          viewportTop = rect.top + rect.height / 2 - tooltipRect.height / 2;
          viewportLeft = rect.right + 10;

          // Check if this position fits
          if (
            viewportLeft + tooltipRect.width > viewport.width - 10 ||
            viewportTop < 10 ||
            viewportTop + tooltipRect.height > viewport.height - 10
          ) {
            fits = false;
          }
          break;

        default:
          fits = false;
      }

      if (fits) {
        finalPosition = pos;
        break;
      }
    }

    // Ensure tooltip stays within viewport bounds as a final safety measure
    viewportTop = Math.max(10, Math.min(viewportTop, viewport.height - tooltipRect.height - 10));
    viewportLeft = Math.max(10, Math.min(viewportLeft, viewport.width - tooltipRect.width - 10));

    // Maintain proper alignment with trigger element when position changes
    // This ensures the arrow still points to the correct edge
    if (finalPosition !== position) {
      switch (finalPosition) {
        case 'top':
          // Align horizontally with trigger center
          viewportLeft = rect.left + rect.width / 2 - tooltipRect.width / 2;
          break;
        case 'bottom':
          // Align horizontally with trigger center
          viewportLeft = rect.left + rect.width / 2 - tooltipRect.width / 2;
          break;
        case 'left':
          // Align vertically with trigger center
          viewportTop = rect.top + rect.height / 2 - tooltipRect.height / 2;
          break;
        case 'right':
          // Align vertically with trigger center
          viewportTop = rect.top + rect.height / 2 - tooltipRect.height / 2;
          break;
      }

      // Re-clamp after alignment adjustments
      viewportTop = Math.max(10, Math.min(viewportTop, viewport.height - tooltipRect.height - 10));
      viewportLeft = Math.max(10, Math.min(viewportLeft, viewport.width - tooltipRect.width - 10));
    }

    // Convert viewport coordinates to container-relative coordinates
    const containerTop = viewportTop;
    const containerLeft = viewportLeft;

    return { tooltipStyle: { top: containerTop, left: containerLeft }, actualPosition: finalPosition };
  };

  const { tooltipStyle, actualPosition } = calculateTooltipPosition();

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Use the actual position for styling, not the requested position
  let positionClassName = `FeedFloToolTip-description--${actualPosition}`;

  if (!['top', 'bottom', 'left', 'right'].includes(actualPosition)) {
    positionClassName = 'FeedFloToolTip-description--top';
    console.warn(`FeedFloTooltip calculated position is invalid: "${actualPosition}", defaulting to top instead`);
  }

  return (
    <>
      <div
        className={`FeedFloToolTip ${darkMode ? 'FeedFloToolTip--dark' : ''} ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={triggerRef}
        {...props}
      >
        {children}
      </div>

      <div
        ref={tooltipRef}
        className={`FeedFloToolTip-description noselect ${multiline ? 'FeedFloToolTip-description--multiline' : ''
          } ${positionClassName} ${textClassName}`}
        style={{
          position: 'fixed',
          top: tooltipStyle.top,
          left: tooltipStyle.left,
          zIndex: 9999,
          visibility: isVisible ? 'visible' : 'hidden',
        }}
      >
        {description}
      </div>
    </>
  );
}

FeedFloToolTip.propTypes = {
  children: PropTypes.node,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  textClassName: PropTypes.string,
  position: PropTypes.string,
  multiline: PropTypes.bool,
  darkMode: PropTypes.bool,
};

export default FeedFloToolTip;
